import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react'

const PARIS_COORDINATE = { lat: 48.864716, lng: 2.349014 }

export type GoogleMapHandle = {
  addMarker: (markerId: string, marker: google.maps.Marker) => void
  createMarker: (lat: number, lng: number, icon: google.maps.Icon | google.maps.Symbol, zIndex?: number) => google.maps.Marker
  removeMarker: (markerId: string) => void
  addCircle: (circleId: string, center: { lat: number; lng: number }, radiusKm: number) => void
  removeCircle: (circleId: string) => void
}

export type GoogleMapProps = {
  onLoad?: (map: google.maps.Map) => void
}

export const GoogleMap = forwardRef(function GoogleMap(props: GoogleMapProps, ref: Ref<GoogleMapHandle>) {
  const { onLoad } = props

  useImperativeHandle(ref, () => ({
    addMarker,
    createMarker,
    removeMarker,
    addCircle,
    removeCircle,
  }))

  const mapContainerRef = useRef<HTMLDivElement>(null)

  const bounds = useRef<google.maps.LatLngBounds | null>(null)
  const markers = useRef<Map<string, google.maps.Marker>>(new Map())
  const circles = useRef<Map<string, google.maps.Circle>>(new Map())

  const [map, setMap] = useState<google.maps.Map | null>(null)

  useEffect(() => {
    if (!mapContainerRef.current || !window.google) return
    bounds.current = new google.maps.LatLngBounds()
    const map = new google.maps.Map(mapContainerRef.current, {
      // styles: mapStyles,
      center: PARIS_COORDINATE,
      zoom: 12,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    })
    setMap(map)
    onLoad?.(map)
  }, [onLoad])

  const _addMarker = (markerId: string, marker: google.maps.Marker) => {
    markers.current.set(markerId, marker)
  }

  const _getMarker = (markerId: string) => {
    return markers.current.get(markerId)
  }

  const _removeMarker = (markerId: string) => {
    if (markers.current.has(markerId)) markers.current.delete(markerId)
  }

  const _fitToMarker = () => {
    if (!map) return

    const newBounds = new google.maps.LatLngBounds()
    markers.current.forEach((marker) => {
      const position = marker.getPosition()
      if (position) newBounds.extend(position)
    })
    if (markers.current.size > 1) {
      map.fitBounds(newBounds)
      map.panToBounds(newBounds)
    } else if (markers.current.size === 1) {
      const marker = markers.current.values().next().value
      const position = marker?.getPosition()
      if (position) {
        map.setCenter(position)
        map.setZoom(14)
      }
    }
    bounds.current = newBounds
  }

  const createMarker = (lat: number, lng: number, icon: google.maps.Icon | google.maps.Symbol, zIndex: number = 3) => {
    const position = { lat, lng }
    bounds.current?.extend(position)
    return new google.maps.Marker({ icon, position, zIndex })
  }

  const addMarker = (markerId: string, marker: google.maps.Marker) => {
    if (!map) return
    marker.setMap(map)
    _addMarker(markerId, marker)
    _fitToMarker()
  }

  const removeMarker = (markerId: string) => {
    if (!markers.current.has(markerId)) return

    const marker = _getMarker(markerId)
    marker?.setMap(null)
    _removeMarker(markerId)
  }

  const addCircle = (circleId: string, center: { lat: number; lng: number }, radiusKm: number) => {
    if (!map) return
    // Remove existing circle with same ID
    if (circles.current.has(circleId)) {
      circles.current.get(circleId)?.setMap(null)
    }
    const circle = new google.maps.Circle({
      map,
      center,
      radius: radiusKm * 1000,
      fillColor: '#4285F4',
      fillOpacity: 0.1,
      strokeColor: '#4285F4',
      strokeOpacity: 0.4,
      strokeWeight: 2,
    })
    circles.current.set(circleId, circle)

    // Fit bounds to include circle
    const circleBounds = circle.getBounds()
    if (circleBounds) {
      map.fitBounds(circleBounds)
    }
  }

  const removeCircle = (circleId: string) => {
    if (!circles.current.has(circleId)) return
    circles.current.get(circleId)?.setMap(null)
    circles.current.delete(circleId)
  }

  return <div ref={mapContainerRef} className="h-full flex-1" />
})

export default GoogleMap
